<template>
  <ContentSection
    :id="item.fields.id ?? item.sys.id"
    :color-mode="productListTheme"
    :class="[
      'content-section--fullwidth',
      { 'product-list-section--full-size': isProductListFullSize }
    ]"
    data-testid="plp-products-list-section"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'internalName'
      })
    "
  >
    <MappedComponents
      v-if="props.item.fields.customContent"
      :items="props.item.fields.customContent"
    />

    <client-only>
      <LibLazyLoad
        root-margin="200px 0px 200px 0px"
        :eager="shouldEagerLoadProductList"
      >
        <Suspense>
          <LazyShopProductsList
            :parent-section-id="item.fields.id ?? item.sys.id"
            :locale="$activeLocale.webapp"
            :store="$shopStore"
            :static-filters="staticFilters"
            :initial-filters="initialFilters"
            :view="productListView"
            :type="productListType"
            :discontinued-specs="discontinuedSpecifications"
            :show-in-a-link="showLinkToInterfaceAdvisorInFilter"
            :lens-selector-in-modal="lensSelectorTool"
            :show-lighting-advisor="showLightingAdvisor"
            :show-camera-advisor="showCameraAdvisor"
            :is-full-size-image="isFullSizeImage"
            :is-full-size="isProductListFullSize"
            :item="item"
          >
            <template #initialLoadingError>
              <TextBlock
                :cols="10"
                :cols-centered="true"
                :content-centered="false"
              >
                <LibRichTextRenderer
                  v-if="initialLoadingErrorRichtext"
                  :rich-text="initialLoadingErrorRichtext"
                />
              </TextBlock>
            </template>
          </LazyShopProductsList>
          <template #fallback>
            <ProductsListSectionSkeleton
              :view="productListView"
              :type="productListType"
              :is-full-size="isProductListFullSize"
            />
          </template>
        </Suspense>
        <template #placeholder>
          <ProductsListSectionSkeleton
            :view="productListView"
            :type="productListType"
            :is-full-size="isProductListFullSize"
          />
        </template>
      </LibLazyLoad>
      <template #placeholder>
        <ProductsListSectionSkeleton
          :view="productListView"
          :type="productListType"
          :is-full-size="isProductListFullSize"
        />
      </template>
    </client-only>
  </ContentSection>
</template>

<script setup lang="ts">
import { ContentSection, TextBlock } from '@hypercodestudio/basler-components';
import { inject, provide } from 'vue';
import { v } from 'h3-valibot';
import { createFiltersFromQuery } from '~/utils/createFiltersFromQuery';
import type { IProductList } from '~~/lib/ContentfulService';
import { parseQuery } from '#vue-router';
import { LAST_TIME_BUY, LAST_TIME_SERVICE } from '~/utils/shop/constants';
import type { ContentSectionColorMode as ColorMode } from '@hypercodestudio/basler-components';

import { LazyShopProductsList } from '#components';
import ProductsListSectionSkeleton from '~/components/shop/skeletons/ProductsListSectionSkeleton.vue';
import {
  createProductFilterStore,
  PRODUCT_FILTER_STORE_INJECT_KEY
} from '~/stores/productFilterStore/productFilterStore';
import {
  createLightingAdvisorStore,
  LIGHTING_ADVISOR_STORE_INJECT_KEY
} from '~/stores/lightingAdvisorStore/lightingAdvisorStore';
import {
  CAMERA_ADVISOR_STORE_INJECT_KEY,
  createCameraAdvisorStore
} from '~/stores/cameraAdvisorStore/cameraAdvisorStore';
import {
  INJECTED_COLOR_MODE,
  PRODUCT_LIST_EMBEDDED_IN_TAB
} from '~~/lib/constants';
import { shouldEagerLoadProductListSection } from '~/utils/shop/shouldEagerLoadProductListSection';
const { $ContentfulLivePreview } = useNuxtApp();

interface Props {
  item: IProductList;
}

const props = defineProps<Props>();

const { $shopStore, $dictionary, $activeLocale } = useNuxtApp();
const route = useRoute();

const parentSectionId = computed(
  () => props.item.fields.id ?? props.item.sys.id
);

const shouldEagerLoadProductList = computed(() =>
  shouldEagerLoadProductListSection(
    route.hash,
    parentSectionId.value,
    route.query
  )
);

const initialLoadingErrorRichtext = computed(
  () => $dictionary.value['productsList.initialLoadingError.message']
);

const filtersSchema = v.record(v.string(), v.array(v.string()));

const guidanceToolEntry = computed(() => props.item?.fields?.guidanceTool);
const guidanceToolType = computed(() =>
  String(guidanceToolEntry.value?.fields?.type ?? '')
);

// `tool` is the legacy iframe component, `toolLensSelector` the native one.
const lensSelectorTool = computed(() =>
  guidanceToolType.value === 'tool' ||
  guidanceToolType.value === 'toolLensSelector'
    ? guidanceToolEntry.value
    : undefined
);

const showLightingAdvisor = computed(
  () => guidanceToolType.value === 'toolLightingAdvisor'
);
const showCameraAdvisor = computed(
  () => guidanceToolType.value === 'toolCameraAdvisor'
);

const staticFilters = computed<Record<string, string[]>>(() => {
  return parseFiltersWithFallback(props.item.fields?.staticFilters, {});
});

const initialFilters = computed<Record<string, string[]>>(() =>
  parseFiltersWithFallback(
    createFiltersFromQuery(
      parseQuery(props.item.fields?.filterQueryParameters ?? '')
    ),
    {}
  )
);

function parseFiltersWithFallback(
  filters: unknown,
  fallback: Record<string, string[]>
) {
  if (!filters) {
    return fallback;
  }

  const parsedFilters = v.safeParse(filtersSchema, filters);
  if (parsedFilters.success) {
    return parsedFilters.output;
  }

  return fallback;
}

const showLinkToInterfaceAdvisorInFilter = computed(() => {
  return props.item.fields.showLinkToInterfaceAdvisorInFilter ?? false;
});
const isFullSizeImage = computed(() => {
  return props.item.fields.fullSizeImage ?? false;
});

const isEmbeddedInTab = inject(PRODUCT_LIST_EMBEDDED_IN_TAB, false);
const isProductListFullSize = computed(() => !isEmbeddedInTab);

const productListTheme = computed(
  // TODO: theme has different values in contentful that are allowed in
  //    the hypercode component
  () => (props.item.fields.theme as ColorMode) ?? 'light'
);
const productListType = computed(
  () => props.item.fields.type ?? 'Specifications'
);
const productListView = computed(() => {
  return props.item.fields.view ?? 'grid';
});
const discontinuedSpecifications = computed(() => {
  return (
    props.item.fields.discontinuedSpecifications ?? [
      LAST_TIME_BUY,
      LAST_TIME_SERVICE
    ]
  );
});

const store = createProductFilterStore(props.item.sys.id);

provide(PRODUCT_FILTER_STORE_INJECT_KEY, store);
provide(
  INJECTED_COLOR_MODE,
  ref(productListTheme.value !== 'white' ? productListTheme.value : 'light')
);

if (showLightingAdvisor.value) {
  const lightingAdvisorStore = createLightingAdvisorStore(
    props.item.sys.id,
    store
  );
  provide(LIGHTING_ADVISOR_STORE_INJECT_KEY, lightingAdvisorStore);
}

if (showCameraAdvisor.value && guidanceToolEntry.value) {
  const cameraAdvisorStore = createCameraAdvisorStore(
    props.item.sys.id,
    store,
    props.item.sys.id,
    guidanceToolEntry.value.fields.configuration
  );
  provide(CAMERA_ADVISOR_STORE_INJECT_KEY, cameraAdvisorStore);
}
</script>

<style>
.tab-panel__content .content-section {
  padding-block: 0px;
}

.tab-panel__content .content-section__inner {
  padding: 0px;
}

/*
 * ProductListLayout full-size styles query the `product-list-section` container.
 * The library creates that container only when the layout is a direct child of
 * `.content-section__inner`. The shop list is nested under lazy-load and form
 * wrappers, so the section establishes the same container itself.
 */
.product-list-section--full-size {
  container-name: product-list-section;
  container-type: inline-size;
}
</style>
